import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppShell } from '@/components/AppShell';
import { MinimalShell } from '@/components/MinimalShell';
import { HomeRedirect, minimalNavFor, RequireBuilder } from '@/components/RequireBuilder';
import { ConfigurePage } from '@/pages/ConfigurePage';
import { HoaListPage } from '@/pages/HoaListPage';
import { HoaDetailPage } from '@/pages/HoaDetailPage';
import { TodosPage } from '@/pages/TodosPage';
import { TestLeasePage } from '@/pages/TestLeasePage';
import { RenewalPage } from '@/pages/RenewalPage';
import { VerificationsPage } from '@/pages/VerificationsPage';
import { PmDirectoryPage } from '@/pages/PmDirectoryPage';
import { ConditionReportsPage } from '@/pages/ConditionReportsPage';
import { RequestsPage } from '@/pages/RequestsPage';
import { AccommodationsPage } from '@/pages/AccommodationsPage';
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { FollowUpsPage } from '@/pages/FollowUpsPage';
import { IntakeFormPage } from '@/pages/IntakeFormPage';
import { RraFormPage } from '@/pages/RraFormPage';
import { RraVerifyFormPage } from '@/pages/RraVerifyFormPage';
import { RraCallPage } from '@/pages/RraCallPage';
import { MoveoutFundsIntakeFormPage } from '@/pages/MoveoutFundsIntakeFormPage';
import { AnimalAddendumFormPage } from '@/pages/AnimalAddendumFormPage';
import { OwnerIntakeFormPage } from '@/pages/OwnerIntakeFormPage';
import { OwnerContactFormPage } from '@/pages/OwnerContactFormPage';
import { CcrUploadPage } from '@/pages/CcrUploadPage';
import { VerificationFormPage } from '@/pages/VerificationFormPage';
import { SignPage } from '@/pages/SignPage';
import { SendHubPage } from '@/pages/SendHubPage';
import { DepositAgreementPage } from '@/pages/DepositAgreementPage';
import { ManualDocPage } from '@/pages/ManualDocPage';
import { PaymentPlanPage } from '@/pages/PaymentPlanPage';
import { AdoptionAddendumPage } from '@/pages/AdoptionAddendumPage';
import { LeaseholderChangePage } from '@/pages/LeaseholderChangePage';
import { LeaseholderRemovalPage } from '@/pages/LeaseholderRemovalPage';
import { AnimalAddendumSendPage } from '@/pages/AnimalAddendumSendPage';
import { PropertyManagementAgreementPage } from '@/pages/PropertyManagementAgreementPage';
import { W9RequestPage } from '@/pages/W9RequestPage';
import { OverviewPage } from '@/pages/OverviewPage';
import { HouseholdsPage } from '@/pages/HouseholdsPage';
import { OwnersPage } from '@/pages/OwnersPage';
import { SignalsPage } from '@/pages/SignalsPage';
import { APPLICATIONS_REDIRECT } from '@/lib/householdsClient';
import { CaseFilePage } from '@/pages/CaseFilePage';
import { HouseholdPage } from '@/pages/HouseholdPage';
import { SentItemsPage } from '@/pages/SentItemsPage';
import { SignersPage } from '@/pages/SignersPage';
import { ItemPage } from '@/pages/ItemPage';
import { LeaseViewPage } from '@/pages/LeaseViewPage';
import {
  AgreementsRedirect,
  DocsRedirect,
  ThreadRedirect,
} from '@/components/sentItems/itemRedirects';

// Layout route: all protected pages share the AppShell (sidenav + header).
// We use a flat route structure (each ProtectedRoute wraps AppShell) for
// symmetry with the existing C1 pattern. An alternative is a nested layout
// route with <Outlet>; this achieves the same outcome without React Router
// nested-route complexity.

/**
 * Every builder page: the AppShell inside the builder gate, so a login whose
 * /api/me capabilities say no builder is sent to Follow-ups (follow-ups plan §5).
 */
function BuilderShell({ children }: { children: ReactNode }) {
  return (
    <RequireBuilder>
      <AppShell>{children}</AppShell>
    </RequireBuilder>
  );
}

/**
 * The Accommodations console for a login WITHOUT the builder but on the RRA
 * roster (Miles, 2026-09-27): the console's API is gated by the roster, not the
 * builder, so such a login keeps it — in the minimal shell, never the builder's.
 */
function AccommodationsWithoutBuilder() {
  const { capabilities } = useAuth();
  return (
    <MinimalShell title="Accommodations" nav={minimalNavFor(capabilities)}>
      <AccommodationsPage />
    </MinimalShell>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/follow-ups" element={<ProtectedRoute><FollowUpsPage /></ProtectedRoute>} />
        {/* PUBLIC applicant-facing pre-lease intake form. No auth shell — the
            token in the path is the only credential (see IntakeFormPage). */}
        <Route path="/intake/:token" element={<IntakeFormPage />} />
        {/* PUBLIC requester-facing accommodation-request form (RRA phase 3).
            Token-gated, no shell; the API re-gates against the live case, so
            the page renders fillable/submitted/closed and never dead-ends. */}
        <Route path="/rra/:token" element={<RraFormPage />} />
        {/* PUBLIC third-party verifier form (RRA). Token-gated, no shell;
            gap-only questions, no free text (see RraVerifyFormPage). */}
        <Route path="/rra-verify/:token" element={<RraVerifyFormPage />} />
        {/* Front-line call sheet (RRA v2.1, F2). The token still addresses the
            verification, but the page now needs the ordinary staff sign-in (and the
            API's RRA_CALLERS roster when set); the caller's name is the sign-in.
            The page keeps its own Shell — no app chrome. */}
        <Route path="/rra-call/:token" element={<ProtectedRoute><RraCallPage /></ProtectedRoute>} />
        {/* PUBLIC resident-facing Move-Out Funds Direction intake (WEL-48).
            Token-gated, no shell — designate the recipient + forwarding address.
            WEL-124: the emailed link is `/moveout-funds/intake/:token` (see
            moveoutIntakeLinkFor), so BOTH paths route to the form — otherwise the
            tenant's link never matches and the page is blank. */}
        <Route path="/moveout-funds/:token" element={<MoveoutFundsIntakeFormPage />} />
        <Route path="/moveout-funds/intake/:token" element={<MoveoutFundsIntakeFormPage />} />
        {/* PUBLIC tenant-facing Animal Addendum form (WEL-153). Token-gated, no
            shell — the household tells us about the new animal (and can attach a
            photo), and the first completion sends the addendum for signature. */}
        <Route path="/animal-addendum/:token" element={<AnimalAddendumFormPage />} />
        {/* WEL-124's lesson, applied ahead of time: the move-out form shipped with
            an emailed link one path segment away from the route, and every tenant
            who clicked it got a blank page. The spec pins the link WITHOUT
            `/intake`, so that is the real route above — this alias costs nothing
            and means a link built from the sibling's helper still lands. */}
        <Route path="/animal-addendum/intake/:token" element={<AnimalAddendumFormPage />} />
        {/* PUBLIC owner-facing intake → EPMA. Token-gated, no shell — the owner
            answers the applicable questions and the agreement auto-generates for
            signature (first-completion-wins). See OwnerIntakeFormPage. */}
        <Route path="/owner-intake/:token" element={<OwnerIntakeFormPage />} />
        {/* PUBLIC owner-facing Owner Contact Addendum intake (chained after the EPMA
            is executed). Token-gated, no shell — the owner confirms the roster + how
            funds/1099/comms are handled; the addendum auto-generates for signature
            (first-completion-wins). See OwnerContactFormPage. */}
        <Route path="/owner-contact/:token" element={<OwnerContactFormPage />} />
        {/* PUBLIC owner-facing DEFERRED CC&R upload. Token-gated, no shell — the owner
            uploads their (new/unlisted) HOA's CC&Rs after the intake completes. See
            CcrUploadPage. */}
        <Route path="/ccr-upload/:token" element={<CcrUploadPage />} />
        {/* PUBLIC previous-landlord rental-verification form. Token-gated, no shell. */}
        <Route path="/verify/:token" element={<VerificationFormPage />} />
        {/* PUBLIC signer-facing signing surface (DocuSeal embed + live status +
            download). No auth shell — the uuid in the path is the only credential. */}
        <Route path="/sign/:uuid" element={<SignPage />} />
        <Route path="/" element={<HomeRedirect />} />
        {/* ── Agreement command center — the areas (Overview, Agreements, Docs,
            Households, Owners & properties, Signals) plus case-file and
            thread-detail leaves. The legacy tool pages below stay routable and
            are reached via the Tools menu or from threads. ── */}
        <Route
          path="/overview"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <OverviewPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Agreements + Docs became Sent Items (Q1). Both routes REDIRECT rather
            than 404: their URLs are in inboxes and Linear comments (T20), and
            the redirects ship in the same deploy as the replacement.
            AgreementsPage.tsx / DocsPage.tsx / ThreadDetailPage.tsx are DELETED
            (W7, 2026-08-29) — these redirects are the only thing those URLs
            reach now, so they are not optional and must not be trimmed to a
            404 "because the pages are gone".
            Each redirect keeps the auth shell it had: the boundary does not
            move, and the Toaster the thread redirect writes to lives there. */}
        <Route
          path="/agreements"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <AgreementsRedirect />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/households"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <HouseholdsPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/households/:householdId"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <HouseholdPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/owners"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <OwnersPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Owner case file — CaseFilePage is caseId-generic; the API returns
            the owner-engagement case payload for owner ids. */}
        <Route
          path="/owners/:caseId"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <CaseFilePage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* /docs?kind=&state= keeps its filters (as `types=`/`state=`); with a
            document open (`sk`/`sid`) it goes straight to that item. */}
        <Route
          path="/docs"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <DocsRedirect />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Sent Items — every item sent or being prepared, one row each
            (replaces Agreements + Docs; W5 owns nav and the redirects). */}
        <Route
          path="/sent-items"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <SentItemsPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Signers — every pending signature at once, longest wait first (sol
            finding 17: the Agreements page's Submissions lens). A SECONDARY
            REPORT over the same corpus, reached from the Sent Items header —
            deliberately not a fourth view, because its row is a signature and
            Sent Items' row is an item. */}
        <Route
          path="/sent-items/signers"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <SignersPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Full Details — absolutely everything about one item: the document
            itself, every version, the signing spine and the lifecycle actions
            (§6.5). Reached from the list, from a household, or by URL. */}
        <Route
          path="/items/:itemId"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <ItemPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* View a lease: header + Review summary card on top + the lease inline
            (Miles, 2026-09-24). Reached from Full Details, the Sent Items panel
            and the lease builder. */}
        <Route
          path="/leases/:groupId"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <LeaseViewPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/signals"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <SignalsPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* A thread is now an item (§7.3). Every feature this page carried lives
            on /items/:itemId; an id with no single item behind it (a rental
            verification's group, an occupancy sync) lands on the list. */}
        <Route
          path="/threads/:threadId"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <ThreadRedirect />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Send hub: pick WHAT to send (lease, deposit, test, renewal, manual). */}
        <Route
          path="/send"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <SendHubPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Send → Deposit agreement: move-out deposit-direction send surface. */}
        <Route
          path="/send/deposit"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <DepositAgreementPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Send → Payment plan: standalone Security Deposit Payment Plan agreement. */}
        <Route
          path="/send/payment-plan"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <PaymentPlanPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Send → Leaseholder change: add/release residents on an existing lease. */}
        <Route
          path="/send/leaseholder-change"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <LeaseholderChangePage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Send → Adoption addendum: ADD residents to a lease, nobody leaving (WEL-131). */}
        <Route
          path="/send/adoption-addendum"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <AdoptionAddendumPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Send → Leaseholder removal: remove an outgoing resident from a lease. */}
        <Route
          path="/send/leaseholder-removal"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <LeaseholderRemovalPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Send → Animal addendum: a current tenant is adding an animal (WEL-153). */}
        <Route
          path="/send/animal-addendum"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <AnimalAddendumSendPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Send → Manual doc: upload-your-own-PDF send (STUB). */}
        <Route
          path="/send/manual"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <ManualDocPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Send → Property management agreement (EPMA): staff-entered, no AppFolio. */}
        <Route
          path="/send/property-management-agreement"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <PropertyManagementAgreementPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Send → Request a W-9 (owner or vendor), owner-sync spec §6. */}
        <Route
          path="/send/w9-request"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <W9RequestPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* The applications list IS the households list, filtered (§8.1).
            Old links, bookmarks and the Send hub's "Lease" tile all land on
            the same rows they used to, with the households around them. */}
        <Route
          path="/applications"
          element={<Navigate to={APPLICATIONS_REDIRECT} replace />}
        />
        <Route
          path="/applications/:groupId/lease"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <ConfigurePage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* D2: HOA management routes */}
        <Route
          path="/hoas"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <HoaListPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/hoas/:hoaId"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <HoaDetailPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* §4: came-of-age to-do queue */}
        <Route
          path="/todos"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <TodosPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* §5: test-lease tool */}
        <Route
          path="/test-lease"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <TestLeasePage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Renewal: send a lease to a unit's existing tenants */}
        <Route
          path="/renewal"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <RenewalPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Unified outbound-requests review surface (all systems) */}
        <Route
          path="/requests"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <RequestsPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* RRA phase 4: the RESTRICTED accommodations console (server-gated by
            the RRA_STAFF allowlist — a non-listed staffer sees the restricted
            notice; the protected data never rides the generic Requests page).
            The one page a login without the builder may still open, when
            /api/me says it is on the roster: then in the minimal shell. */}
        <Route
          path="/accommodations"
          element={
            <ProtectedRoute>
              <RequireBuilder
                withoutBuilder={{ capability: 'accommodations', element: <AccommodationsWithoutBuilder /> }}
              >
                <AppShell>
                  <AccommodationsPage />
                </AppShell>
              </RequireBuilder>
            </ProtectedRoute>
          }
        />
        {/* Phase 4: rental-verification review surface */}
        <Route
          path="/verifications"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <VerificationsPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Phase 4: PM company directory CRUD */}
        <Route
          path="/pm-directory"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <PmDirectoryPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        {/* Condition reports: staff tracking + exceptions for move-in inspections */}
        <Route
          path="/condition-reports"
          element={
            <ProtectedRoute>
              <BuilderShell>
                <ConditionReportsPage />
              </BuilderShell>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </AuthProvider>
  );
}
